import { useEffect, useState, useCallback } from "react";
import * as XLSX from "xlsx";

import useBidStore from "@/stores/Bid";
import { useLoadingStore } from "@/stores/Loading";

import { BidContent, GetBidListRequest } from "@/types/bid";
import { SearchConditionItem } from "@/types/setting";

import { isNotEmpty, formatDate } from "@/libs/utils";

import { showToast } from "@/utils/toastUtils";

import { GetBidList } from "@/services/Bid";
import { GetSearchConditionList, SetSearchCondition } from "@/services/Setting";

import {
  DomesticTabData,
  DefaultBidSearchKeywordList,
  getSearchFilterToString,
  getSearchConditionFilterToString,
  ProjectType,
  DefaultBidSearch,
} from "@/consts/Bid";

import ContentLayout from "@/layouts/ContentLayout";

import BidList from "@/pages/Bid/List";

import CommonTab from "@/components/Common/Tab";
import BidSearch from "@/components/BidSearch";

export default function Bid() {
  const { startLoading, endLoading } = useLoadingStore();

  const storedFilter = useBidStore((state) => state.filter);
  const setStoredFilter = useBidStore((state) => state.setFilter);
  const storedPage = useBidStore((state) => state.bidPage);
  const setStoredPage = useBidStore((state) => state.setBidPage);
  const storedResult = useBidStore((state) => state.bidResult);
  const setStoredResult = useBidStore((state) => state.setBidResult);

  const [searchConditionList, setSearchConditionList] = useState<
    SearchConditionItem[]
  >([]);
  const [bidContent, setBidContent] = useState<BidContent>();

  const handleSearch = async (
    toastFinish: boolean = false,
    newFilter?: GetBidListRequest
  ) => {
    try {
      startLoading("bid-search");
      const targetFilter = newFilter ?? storedFilter;
      const searchKeywordString = getSearchFilterToString(targetFilter);
      const result = await GetBidList({ input: searchKeywordString });
      if (result.data.success) {
        setBidContent(result.data.pods[0].content);
        setStoredResult(result.data.pods[0].content);
        setStoredPage(storedFilter.settings.page);
        if (toastFinish) showToast({ message: "공고 검색이 완료되었습니다." });
      } else {
        // TODO:: Error
      }
    } catch (e) {
    } finally {
      endLoading("bid-search");
    }
  };

  const handleSaveSearchCondition = async () => {
    try {
      if (!isNotEmpty(storedFilter.search_id)) {
        showToast({ message: "검색 조건을 만들어주세요." });
        return;
      }
      const params = getSearchConditionFilterToString(storedFilter);
      const result = await SetSearchCondition(params);
      if (result.data.success) {
        showToast({
          message: "검색 조건이 저장되었습니다.",
        });
        getSearchConditionList(false);
      } else {
        showToast({
          type: "error",
          message: `검색 조건 저장을 실패했습니다. ${result.data.error.content}`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: `검색 조건 저장을 실패했습니다. ${e}`,
      });
    } finally {
    }
  };

  const handleChangeSearchCondition = (
    targetConditionList: SearchConditionItem[],
    targetConditionId?: string
  ) => {
    if (isNotEmpty(targetConditionId)) {
      setStoredFilter(null);
    }
    const targetFilter =
      targetConditionId || !isNotEmpty(storedFilter?.search_id)
        ? null
        : storedFilter;
    const targetSearchCondition = targetFilter ??
      (targetConditionId
        ? targetConditionList.filter(
            (condition) => condition.search_id === targetConditionId
          )[0]
        : targetConditionList.filter((item) => item.pinned)[0]) ??
      targetConditionList[0] ?? {
        settings: DefaultBidSearch,
      };
    const {
      settings: { keyword_sets, ...otherSettings },
    } = targetSearchCondition;
    setStoredFilter({
      ...targetSearchCondition,
      settings: {
        ...otherSettings,
        page: storedPage,
        page_size: DefaultBidSearch.page_size,
        project_type: ProjectType.Original,
        keyword_sets: DefaultBidSearchKeywordList.map(
          (keyword, keywordIndex) => keyword_sets[keywordIndex] ?? keyword
        ),
      },
    });
  };

  const getSearchConditionList = async (refresh: boolean) => {
    try {
      const result = await GetSearchConditionList();
      if (result.data.success) {
        const resultItem = result.data.pods[0].content.items;
        setSearchConditionList(resultItem);
        // 먼저 생성한 검색 조건이 없는 경우엔 무조건 refresh
        if (refresh || !isNotEmpty(storedFilter?.search_id)) {
          handleChangeSearchCondition(resultItem);
        }
      }
    } catch (e) {
    } finally {
    }
  };

  // 엑셀 다운로드 - 전체 공고 목록
  const handleExcelDownload = useCallback(async () => {
    if (!bidContent || bidContent.count === 0) {
      showToast({ type: "error", message: "다운로드할 공고가 없습니다." });
      return;
    }

    try {
      startLoading("excel-download");
      showToast({ message: "전체 공고 목록을 다운로드 중입니다..." });

      // 전체 데이터를 가져오기 위해 page_size를 전체 개수로 설정
      const searchKeywordString = getSearchFilterToString({
        ...storedFilter,
        settings: {
          ...storedFilter.settings,
          page: 1,
          page_size: bidContent.count, // 전체 개수로 설정
        },
      });

      const result = await GetBidList({ input: searchKeywordString });

      if (result.data.success) {
        const allItems = result.data.pods[0].content.items;

        // 엑셀 데이터 구성
        const exportData = allItems.map((item: any, index: number) => ({
          "순번": index + 1,
          "공고번호": item.notice_no || "-",
          "공고명": item.title || "-",
          "발주처": item.client_name || "-",
          "사업금액": item.budget_amount ? `${item.budget_amount.toLocaleString()}원` : "-",
          "사업구분": item.category_name || "-",
          "게시일": item.uploaded_dt ? formatDate(item.uploaded_dt, "yyyy-MM-dd") : "-",
          "마감일": item.close_dt ? formatDate(item.close_dt, "yyyy-MM-dd") : "-",
          "자사제품비중": item.own_product_ratio != null ? `${item.own_product_ratio}%` : "-",
          "업종조건": item.license_match ? "충족" : "미충족",
          "물품조건": item.product_match ? "충족" : "미충족",
        }));

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "공고목록");

        // 컬럼 너비 설정
        worksheet["!cols"] = [
          { wch: 6 },  // 순번
          { wch: 20 }, // 공고번호
          { wch: 50 }, // 공고명
          { wch: 25 }, // 발주처
          { wch: 15 }, // 사업금액
          { wch: 10 }, // 사업구분
          { wch: 12 }, // 게시일
          { wch: 12 }, // 마감일
          { wch: 12 }, // 자사제품비중
          { wch: 10 }, // 업종조건
          { wch: 10 }, // 물품조건
        ];

        const fileName = `입찰공고_목록_${formatDate(new Date().getTime(), "yyyyMMdd_HHmmss")}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        showToast({
          type: "success",
          message: `${allItems.length}건의 공고를 다운로드했습니다.`,
        });
      }
    } catch (e) {
      showToast({
        type: "error",
        message: "엑셀 다운로드에 실패했습니다.",
      });
    } finally {
      endLoading("excel-download");
    }
  }, [bidContent, storedFilter, startLoading, endLoading]);

  useEffect(() => {
    getSearchConditionList(true);
    if (isNotEmpty(storedResult)) {
      setBidContent(storedResult);
    }
  }, []);

  return (
    <ContentLayout>
      <CommonTab variants="string" data={DomesticTabData} />
      <BidSearch
        projectType={ProjectType.Original}
        onSearch={handleSearch}
        totalItems={bidContent?.count}
        searchConditionList={searchConditionList}
        onChangeCondition={(searchId) => {
          handleChangeSearchCondition(searchConditionList, searchId);
        }}
        onSaveSearchCondition={handleSaveSearchCondition}
        onRefreshSearchCondition={() => getSearchConditionList(false)}
        onExcelDownload={handleExcelDownload}
      >
        {bidContent && (
          <BidList
            content={bidContent}
            onSearch={handleSearch}
            projectType={storedFilter.settings.project_type}
          />
        )}
      </BidSearch>
    </ContentLayout>
  );
}
